import { AutoComplete, Card, Col, Form, Image, Input, Row, Select } from 'antd';
import FloatingFormItem from '../../FloatingFormItem';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import VehicleColorsSelect from '../../VehicleColorsSelect';
import { Model } from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.types';
import { useState } from 'react';
import FuelsSelect from '../../FuelsSelect';
import AccessoriesSelect from '../../AccessoriesSelect';
import { useIntl } from 'react-intl';
import MaskedInput from 'react-text-mask';
import { licensePlateMask } from '@/utils/mask/commonMasks';
import {
  CustomerVehicle,
  VehicleFormData,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import BrandsInfiniteSelect from '../../BrandsInfiniteSelect';
import { VehicleType } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import ModelsInfiniteSelect from '../../ModelsInfiniteSelect';
import InputDecimal from '../../InputDecimal';

import './style.less';
import { isAppPortocar } from '@/index';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { getUserFromLocalStorage } from '@/utils/user';

interface Props {
  disabledFields?: boolean;
  isEdit?: boolean;
  vehicle?: CustomerVehicle;
}

const VehicleForm: React.FC<Props> = ({
  disabledFields = false,
  isEdit = false,
  vehicle,
}) => {
  const form = Form.useFormInstance<VehicleFormData>();
  const formValues = Form.useWatch([], form);
  const intl = useIntl();
  const [modelYears, setModelYears] = useState<[number, number]>();

  const userFromLocalStorage = getUserFromLocalStorage();
  const isMasterPasswordAuthentication =
    userFromLocalStorage?.isMasterPasswordAuthentication === 'true';
  const disableEditOnPortocar =
    isAppPortocar && !!isEdit && !isMasterPasswordAuthentication;

  const searchBy = Form.useWatch('searchBy', form);

  const modelYearsDiff = modelYears ? modelYears[1] - modelYears[0] : 0;
  const modelYearsOptions = modelYears?.length
    ? Array.from({ length: modelYearsDiff + 1 })
        .fill(1, modelYears[0], modelYears[1])
        .map((_, i) => modelYears[0] + i)
    : [];

  const hasLicensePlate = !!formValues?.licensePlate;

  const hasChassi = !!formValues?.chassi && formValues.chassi.length === 17;

  const handlerDisableFields =
    searchBy === 'Placa' ? !hasLicensePlate : !hasChassi;

  const requiredField = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const { data: matrixData } = useMatrix();

  function handleModel(model?: Model) {
    if (model) {
      setModelYears([model.yearStartFabrication, model.yearEndFabrication]);

      if ((!formValues.image || !formValues.imageUpload) && !!model.image) {
        form.setFieldValue('image', model.image);
      }
    }
  }

  const yearFabrication = Form.useWatch('yearFabrication', form);
  const vehicleImage = formValues?.model?.brand?.image;

  return (
    <Card
      title="Dados do Veículo"
      className="vehicle-form-card-container"
      extra={
        vehicleImage && (
          <Image
            src={formValues?.model?.brand?.image}
            preview={false}
            width={60}
          />
        )
      }
    >
      <Form.Item hidden name="modelName" />
      <Form.Item hidden name="brandName" />
      <Form.Item hidden name="customerId" />
      <Form.Item hidden name="vehicleId" />
      <Form.Item hidden name="image" />
      <Form.Item hidden name="color" />
      <Form.Item hidden name="model" />
      <Form.Item hidden name="fuel" />

      <Row gutter={[12, 12]}>
        <Col span={24} md={12}>
          <FloatingFormItem
            name="licensePlate"
            label="Placa"
            getValueFromEvent={(e) => String(e.target.value).toUpperCase()}
            rules={[
              requiredField,
              {
                min: 1,
                max: 7,
                message: intl.formatMessage({
                  id: 'commons.form.errors.invalidLicensePlate',
                }),
              },
            ]}
          >
            <MaskedInput
              className="ant-input"
              mask={licensePlateMask}
              guide={false}
              disabled={
                disableEditOnPortocar
                  ? !!vehicle?.licensePlate
                  : disabledFields || handlerDisableFields
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            name="chassi"
            label="Chassi"
            rules={[defaultMaxLenghtFieldError(30)]}
          >
            <Input
              disabled={
                disableEditOnPortocar
                  ? !!vehicle?.chassi
                  : disabledFields || handlerDisableFields
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            name="brandId"
            label="Marca"
            rules={[requiredField]}
            getValueFromEvent={(value) => {
              setModelYears(undefined);
              form.resetFields([
                'modelId',
                'yearFabrication',
                'yearModel',
                'fuelId',
                'colorId',
                'model',
                'image',
                'imageUpload',
              ]);
              return value;
            }}
          >
            <BrandsInfiniteSelect
              disabled={
                disableEditOnPortocar || disabledFields || handlerDisableFields
              }
              onBrandSelected={(brand) => {
                const model = formValues.model;

                form.setFieldValue('model', { ...model, brand });
              }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            name="modelId"
            label="Modelo"
            rules={[requiredField]}
            getValueFromEvent={(value) => {
              setModelYears(undefined);
              form.resetFields([
                'yearFabrication',
                'yearModel',
                'fuelId',
                'colorId',
                'image',
                'imageUpload',
              ]);
              return value;
            }}
          >
            <ModelsInfiniteSelect
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
              queryFilter={{ brandId: formValues?.brandId }}
              onModelSelect={handleModel}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="yearFabrication"
            label="Ano de fabricação"
            rules={[requiredField]}
          >
            <AutoComplete
              showSearch
              optionFilterProp="label"
              options={modelYearsOptions.map((year) => ({
                value: year,
                label: year,
              }))}
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="yearModel"
            label="Ano do modelo"
            rules={[
              requiredField,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (
                    !!value &&
                    Number(value) >= Number(getFieldValue('yearFabrication'))
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    `Digite um valor maior ou igual a ${Number(
                      getFieldValue('yearFabrication'),
                    )}`,
                  );
                },
              }),
            ]}
          >
            <AutoComplete
              showSearch
              optionFilterProp="label"
              options={modelYearsOptions
                .filter((year) => year >= Number(yearFabrication))
                .map((year) => ({
                  value: year,
                  label: year,
                }))}
              disabled={
                !disableEditOnPortocar &&
                (disabledFields ||
                  handlerDisableFields ||
                  !formValues.yearFabrication)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            name="fuelId"
            label="Combustível"
            rules={[requiredField]}
          >
            <FuelsSelect
              useCachedData
              disabled={
                disableEditOnPortocar
                  ? !!vehicle?.fuel.id &&
                    !vehicle.fuel.name?.toLowerCase()?.includes('indefinido')
                  : disabledFields || handlerDisableFields
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem name="colorId" label="Cor" rules={[requiredField]}>
            <VehicleColorsSelect
              disabled={
                disableEditOnPortocar
                  ? !!vehicle?.color.id &&
                    !vehicle.color.name.includes('undefined')
                  : disabledFields || handlerDisableFields
              }
              onSelectedColor={(color) => {
                form.setFieldValue('color', color);
              }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            name="release"
            label="Geração"
            rules={[defaultMaxLenghtFieldError(10)]}
          >
            <Input
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            name="renavam"
            label="Renavam"
            rules={[defaultMaxLenghtFieldError(11)]}
          >
            <Input
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            name="averageKm"
            label="Média do KM"
            rules={[{ type: 'number', min: 0 }]}
          >
            <InputDecimal
              fractions={0}
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            name="km"
            label="KM"
            rules={[
              { type: 'number', min: 0 },
              {
                ...requiredField,

                required: matrixData?.matrixSettings.vehicleKmIsRequired,
              },
            ]}
          >
            <InputDecimal
              fractions={0}
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem name="type" label="Tipo do veículo">
            <Select
              allowClear
              options={[
                { label: 'Hatch', value: VehicleType.HATCH },
                { label: 'Pickup', value: VehicleType.PICKUP },
                { label: 'Sedan', value: VehicleType.SEDAN },
                { label: 'SUV', value: VehicleType.SUV },
                { label: 'Box Truck', value: VehicleType.BOX_TRUCK },
                { label: 'Truck', value: VehicleType.TRUCK },
                { label: 'Naked', value: VehicleType.MOTO_NAKED },
                { label: 'Scooter', value: VehicleType.MOTO_SCOOTER },
              ]}
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={12}>
          <FloatingFormItem
            name="fleet"
            label="Frota"
            rules={[defaultMaxLenghtFieldError(20)]}
          >
            <Input
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={12}>
          <FloatingFormItem name="accessories" label="Acessórios">
            <AccessoriesSelect
              useCachedData
              disabled={
                !disableEditOnPortocar &&
                (disabledFields || handlerDisableFields)
              }
              maxTagCount="responsive"
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </Card>
  );
};

export default VehicleForm;
